// Single source of truth for the app's public (logged-out-accessible) routes.
// This list MUST mirror the entries flagged `public: true` in
// src/app/aggregators/pages.ts (redirect-only entries excluded). The
// public-routes.spec.ts test enforces that mirror and fails CI if the two
// lists diverge — do not drop that guard when editing this list.
//
// Used to suppress auth-gating side effects while the user is legitimately on
// one of these pages:
//  - the stale-token 401 handler must not fire auth:logout mid-typing
//    (http-client.ts)
//  - the logged-out branded background + signup auth modal must not mount over
//    a public page such as the plain login form or a shared checklist link
//    (app.component.ts, Issue 52 / shared-checklist regression)
export const PUBLIC_ROUTE_PREFIXES = [
  // Auth / onboarding
  'introduction',
  'login',
  'forgot-password',
  'create-account',
  'password-update',
  'email',
  'capacitor-test',
  // Misc public pages
  'report-issue',
  'success',
  // Shared-link family (checklists, to-do lists, jobs, contact-client)
  'checklist',
  'to-do-list',
  'shared-to-do-list',
  'complete-to-do-list',
  'job',
  'shared-job',
  'shared-contact-client',
  'shared-complete-job',
];

/**
 * True when the current location hash (or a provided hash) points at a public
 * route. Matches an exact prefix (`#/login`) or a sub-path (`#/login/:token`),
 * so both the plain login route and the token-login route count as public.
 */
export function isOnPublicRoute(hash?: string): boolean {
  const rawHash = hash ?? ((typeof window !== 'undefined' && window.location?.hash) || '');
  const route = rawHash.replace(/^#\/?/, '').split('?')[0];
  return PUBLIC_ROUTE_PREFIXES.some(prefix => route === prefix || route.startsWith(prefix + '/'));
}
